/**
 * ProducerRow — channel avatar + name + verified check + Follow button.
 *
 * Sits below the title in the meta block. Follow (auth, follow flow, counter cache) lives in VIEWER_AUTH_SPEC.md and
 * ships as a follow-up; until it is wired the row shows no Follow button, since a button that does nothing is not a
 * control. `showFollow` and `followLabel` stay in the config for then.
 */

import { BadgeCheck } from 'lucide-react'
import {
  registerPrimitive,
  type PrimitiveProps,
} from '../../layout'
import { useEvent } from '../../destination'

export interface ProducerRowConfig {
  /** Render the Follow button once following is wired. Ignored until then. */
  showFollow?: boolean
  /** Label override for the follow CTA. Default "Follow". */
  followLabel?: string
}

function Initials({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 3)
    .join('')
    .toUpperCase()
  return <span style={{ fontWeight: 800, fontSize: 14 }}>{initials || '?'}</span>
}

export function ProducerRow(_props: PrimitiveProps) {
  const event = useEvent()

  const org = event.organization
  const displayName = org.displayName ?? org.name

  return (
    <div className="lv-producer">
      {org.logoUrl ? (
        <img className="lv-producer-avatar" src={org.logoUrl} alt="" />
      ) : (
        <div className="lv-producer-avatar lv-producer-avatar-fallback">
          <Initials name={displayName} />
        </div>
      )}
      <div>
        <div className="lv-producer-name">
          {displayName}
          {org.verified && (
            <BadgeCheck role="img" aria-label="Verified" />
          )}
        </div>
        {org.eventsCount > 0 && (
          <div className="lv-producer-stat">
            {org.eventsCount.toLocaleString()} {org.eventsCount === 1 ? 'event' : 'events'} streamed
          </div>
        )}
      </div>
    </div>
  )
}

registerPrimitive('ProducerRow', ProducerRow)
